import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const FirstLogin = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { user, handleFirstLogin, needsFirstLogin } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  // Extract first-login token from URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const firstLoginToken = urlParams.get('token');

  // If no token in URL and user doesn't need first login, redirect to dashboard
  React.useEffect(() => {
    if (!firstLoginToken && !needsFirstLogin()) {
      navigate('/dashboard');
    }
  }, [firstLoginToken, needsFirstLogin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Token is only required if user is not authenticated
    // If user is authenticated but needs first login, no token is needed
    if (!firstLoginToken && !user) {
      setError('Invalid or missing first-login token. Please use the link from your email.');
      return;
    }

    setLoading(true);

    try {
      const result = await handleFirstLogin(firstLoginToken, newPassword);
      
      if (result.success) {
        // Redirect to dashboard after successful password change
        navigate('/dashboard');
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            Welcome to AI examr
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.secondary, mb: 3 }}>
            Complete Your Account Setup
          </Typography>

          {user && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom>
                Hello, <strong>{user.username}</strong>!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please set a new password to complete your account setup.
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Password must be at least 8 characters long"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Setting Password...
                </>
              ) : (
                'Set Password & Complete Setup'
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              After setting your password, you'll have full access to the system.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FirstLogin;